import { Code, ConnectError, Transport, createCallbackClient } from "@connectrpc/connect"
import { useToasts } from "components/Toast"
import { StreamVehicleLiveDataResponse } from "gen/einride/rd_operator_interface/v1/vehicle_live_pb"
import { VehicleService } from "gen/einride/rd_operator_interface/v1/vehicle_service_pb"
import { useAPITransport } from "lib/api/hooks/useAPITransport"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Region } from "../client"

interface Props {
  regionVehicle: Region
  name: string | undefined
  // Gets called every time a message arrives.
  onNewData: (data: StreamVehicleLiveDataResponse) => void
  // Gets called on end of stream, regardless of error.
  onStreamEnd?: (err?: ConnectError) => void
}

const MAX_RETRIES = 5
const MAX_SECONDS_BETWEEN_DATA = 30

// Long-lived streams get torn down by infrastructure as a matter of course:
// Cloud Run / the API-Gateway has a 300 second timeout, proxies reset idle
// connections, the network blips, etc. connect-web surfaces these in a few
// different shapes depending on whether the backend managed to send its closing
// trailer before the connection died:
//   - Code.DeadlineExceeded            (gateway timeout, clean)
//   - "missing trailer"                (gateway killed it before the trailer)
//   - Code.Unknown "network error"     (browser fetch failed, e.g. "Failed to fetch")
//   - Code.Unavailable / Code.Canceled (connection reset)
// None of these are real errors — they are expected for streaming and are
// handled by the reconnect logic below, so we must not surface them to the user.
export const isTransientStreamError = (err: ConnectError): boolean => {
  if (
    err.code === Code.DeadlineExceeded ||
    err.code === Code.Unavailable ||
    err.code === Code.Canceled
  ) {
    return true
  }
  const message = err.message.toLowerCase()
  return (
    message.includes("missing trailer") ||
    message.includes("network error") ||
    message.includes("failed to fetch")
  )
}

export const useStreamVehicleLiveData = (props: Props): void => {
  const { regionVehicle, name, onNewData, onStreamEnd } = props
  const ts: Transport = useAPITransport(regionVehicle)
  const client = useMemo(() => createCallbackClient(VehicleService, ts), [ts])
  const lastStreamMessageRef = useRef<Date | undefined>(undefined)
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const cancelStreamRef = useRef<(() => void) | null>(null)
  const toastIDRef = useRef<string>(Date.now().toString())

  const { toast, dismiss } = useToasts()

  const handleNewData = useCallback(
    (data: StreamVehicleLiveDataResponse) => {
      const now = new Date()
      lastStreamMessageRef.current = now
      if (retryAttempt > 0) {
        setRetryAttempt(0)
        setIsReconnecting(false)
        // Dismiss any reconnecting toasts
        dismiss(toastIDRef.current)
      }
      onNewData(data)
    },
    [onNewData, retryAttempt, dismiss],
  )

  const handleStreamEnd = useCallback(
    (err?: ConnectError) => {
      if (err && retryAttempt < MAX_RETRIES) {
        setRetryAttempt((prev) => prev + 1)
        setIsReconnecting(true)
      } else if (retryAttempt >= MAX_RETRIES) {
        toast({
          message: "Failed to reconnect after multiple attempts",
          status: "fail",
          id: toastIDRef.current,
          keepCount: false,
        })
        setIsReconnecting(false)
      }

      if (err && !isTransientStreamError(err)) {
        // Only genuinely unexpected errors reach the user. Transient connection
        // drops are silently reconnected (see isTransientStreamError); if every
        // reconnect attempt fails, the "Failed to reconnect after multiple
        // attempts" toast above is what informs the user.
        toast({
          status: "fail",
          message: `live data: ${err.message ?? "unknown livedata error"}`,
        })
      }
      if (onStreamEnd) onStreamEnd(err)
    },
    [onStreamEnd, retryAttempt, toast],
  )

  useEffect(() => {
    const checkMessageTime = (): void => {
      if (!lastStreamMessageRef.current || isReconnecting) return
      const now = new Date()
      const diff = Math.round((now.getTime() - lastStreamMessageRef.current.getTime()) / 1000)

      // If we go more than MAX_SECONDS_BETWEEN_DATA without live data, silently
      // attempt to reconnect. We don't toast here: routine stream teardowns are
      // expected and the reconnect almost always succeeds; only a definitive
      // failure (the "Failed to reconnect after multiple attempts" toast)
      // should reach the user.
      if (diff > MAX_SECONDS_BETWEEN_DATA) {
        setRetryAttempt((prev) => prev + 1)
        setIsReconnecting(true)
      } else {
        dismiss(toastIDRef.current)
      }
    }

    const interval = setInterval(checkMessageTime, 1000)
    return () => clearInterval(interval)
  }, [isReconnecting, dismiss])

  useEffect(() => {
    if (!name || retryAttempt > MAX_RETRIES) {
      return undefined
    }

    const delay = 2 ** retryAttempt * 1000

    // Reconnect silently — no per-attempt toast. Routine drops are expected and
    // reconnect almost always succeeds; the user is only told once every retry
    // has failed (see the "Failed to reconnect after multiple attempts" toast).
    const timeoutId = setTimeout(() => {
      if (cancelStreamRef.current) {
        cancelStreamRef.current()
      }
      const cancel = client.streamVehicleLiveData({ name }, handleNewData, handleStreamEnd)
      cancelStreamRef.current = cancel
    }, delay)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [client, name, retryAttempt, isReconnecting, handleNewData, handleStreamEnd])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (cancelStreamRef.current) {
        cancelStreamRef.current()
      }
    }
  }, [])
}
